import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import TextInput from "../inputs/textInput";
import { RequiredIndicator } from "../inputs/requiredIndicator";
import PrimaryButton from "../buttons/primaryButton";

export interface CustomerDetails {
  billingFirstName: string;
  billingLastName: string;
  email: string;
  mobilePhone: string;
  billingAddress1: string;
  billingCity: string;
  billingPostalCode: string;
  billingCountryCode: string;
}

interface CustomerDetailsProps {
  countries: any[];
  onSubmit: (details: CustomerDetails) => void;
  errorMessages: string[];
  initialValues?: Partial<CustomerDetails>;
}

const CustomerDetailsForm: React.FC<CustomerDetailsProps> = ({ countries, onSubmit, errorMessages, initialValues }) => {
  const { t } = useTranslation();

  // Depend on individual field values rather than the `initialValues` object
  // reference. The parent derives `initialValues` via `useMemo`, so its
  // reference changes whenever any upstream dependency (e.g. `countries`,
  // `bookingData`) updates — even when the actual field values are unchanged.
  // Resetting on every reference change would silently wipe user-entered data.
  const billingFirstName = initialValues?.billingFirstName ?? "";
  const billingLastName = initialValues?.billingLastName ?? "";
  const email = initialValues?.email ?? "";
  const mobilePhone = initialValues?.mobilePhone ?? "";
  const billingAddress1 = initialValues?.billingAddress1 ?? "";
  const billingCity = initialValues?.billingCity ?? "";
  const billingPostalCode = initialValues?.billingPostalCode ?? "";
  const billingCountryCode = initialValues?.billingCountryCode ?? "";

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<CustomerDetails>({
    defaultValues: {
      billingFirstName,
      billingLastName,
      email,
      mobilePhone,
      billingAddress1,
      billingCity,
      billingPostalCode,
      billingCountryCode,
    },
  });

  useEffect(() => {
    reset({
      billingFirstName,
      billingLastName,
      email,
      mobilePhone,
      billingAddress1,
      billingCity,
      billingPostalCode,
      billingCountryCode,
    });
  }, [
    billingFirstName,
    billingLastName,
    email,
    mobilePhone,
    billingAddress1,
    billingCity,
    billingPostalCode,
    billingCountryCode,
    reset,
  ]);

  const nameValidation = {
    required: t("field_required", "This field is required"),
    pattern: {
      value: /^[A-Za-z]+$/,
      message: t("letters_only", "Must contain only letters"),
    },
  };

  const emailValidation = {
    required: t("email_required", "Email is required"),
    pattern: {
      value: /^[^\s@]+@([^\s@.]+\.)+[^\s@.]+$/,
      message: t("invalid_email", "Invalid email format"),
    },
  };

  const phoneValidation = {
    required: t("mobile_phone_required", "Mobile Phone is required"),
    pattern: {
      value: /^\+\d{1,15}$/,
      message: t(
        "mobile_phone_format",
        "Mobile Phone must include a country code starting with + and a maximum of 15 digits",
      ),
    },
  };

  const requiredValidation = {
    required: t("field_required", "This field is required"),
  };

  const onFormSubmit = (data: CustomerDetails) => {
    onSubmit(data);
  };

  return (
    <div id="gc-fp-billing-form">
      <div
        id="address-info"
        className="gcw:rounded-lg gcw:border gcw:border-gray-200 gcw:bg-white gcw:shadow-sm gcw:p-bs-3"
      >
        <p className="gcw:text-lg gcw:font-bold gcw:pt-2">
          {t("confirm_billing_address", "Confirm your billing address:")}
        </p>
        <form id="addressForm" onSubmit={handleSubmit(onFormSubmit)}>
          <div className="gcw:grid gcw:grid-cols-1 gcw:sm:grid-cols-2 gcw:gap-bs-3 gcw:mb-bs-3">
            <TextInput
              id="billingFirstName"
              name="billingFirstName"
              label={t("first_name", "First Name")}
              placeholder={t("first_name", "First Name")}
              required
              className="gcw:w-full"
              labelClassName="gcw:font-bold gcw:text-body"
              register={register}
              error={errors.billingFirstName}
              validation={nameValidation}
            />
            <TextInput
              id="billingLastName"
              name="billingLastName"
              label={t("last_name", "Last Name")}
              placeholder={t("last_name", "Last Name")}
              required
              className="gcw:w-full"
              labelClassName="gcw:font-bold gcw:text-body"
              register={register}
              error={errors.billingLastName}
              validation={nameValidation}
            />
          </div>
          <div className="gcw:mb-bs-3">
            <TextInput
              id="email"
              name="email"
              label={t("email", "Email")}
              placeholder={t("email", "Email")}
              type="email"
              required
              className="gcw:w-full"
              labelClassName="gcw:font-bold gcw:text-body"
              register={register}
              error={errors.email}
              validation={emailValidation}
            />
          </div>
          <div className="gcw:mb-bs-3">
            <TextInput
              id="mobilePhone"
              name="mobilePhone"
              label={t("mobile_phone", "Mobile Phone")}
              placeholder={t("mobile_phone", "Mobile Phone")}
              required
              className="gcw:w-full"
              labelClassName="gcw:font-bold gcw:text-body"
              register={register}
              error={errors.mobilePhone}
              validation={phoneValidation}
            />
          </div>
          <div className="gcw:mb-bs-3">
            <TextInput
              id="billingAddress1"
              name="billingAddress1"
              label={t("address", "Address")}
              placeholder={t("address", "Address")}
              required
              className="gcw:w-full"
              labelClassName="gcw:font-bold gcw:text-body"
              register={register}
              error={errors.billingAddress1}
              validation={requiredValidation}
            />
          </div>
          <div className="gcw:mb-bs-3">
            <div className="gcw:w-full gcw:mb-bs-3">
              <label className="gcw:font-bold gcw:text-body" htmlFor="billingCountryCode">
                {t("country", "Country")}
                <RequiredIndicator />
              </label>
              <Controller
                name="billingCountryCode"
                control={control}
                rules={requiredValidation}
                render={({ field }) => (
                  <select
                    {...field}
                    id="billingCountryCode"
                    className={`container-inputs ${errors.billingCountryCode ? "gcw:border-danger" : ""}`}
                    style={{
                      color: !field.value ? "#6c757d" : "#1B232A",
                    }}
                  >
                    <option value="">{t("select_country", "Select Country")}</option>
                    {countries.map((country: any) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.billingCountryCode && (
                <div className="gcw:text-error gcw:text-sm gcw:mt-1">{errors.billingCountryCode.message}</div>
              )}
            </div>
          </div>
          <div className="gcw:grid gcw:grid-cols-1 gcw:sm:grid-cols-2 gcw:gap-bs-3 gcw:mb-bs-3">
            <TextInput
              id="billingCity"
              name="billingCity"
              label={t("city", "City")}
              placeholder={t("city", "City")}
              required
              className="gcw:w-full"
              labelClassName="gcw:font-bold gcw:text-body"
              register={register}
              error={errors.billingCity}
              validation={requiredValidation}
            />
            <TextInput
              id="billingPostalCode"
              name="billingPostalCode"
              label={t("postal_code", "Postal Code")}
              placeholder={t("postal_code", "Postal Code")}
              required
              className="gcw:w-full"
              labelClassName="gcw:font-bold gcw:text-body"
              register={register}
              error={errors.billingPostalCode}
              validation={requiredValidation}
            />
          </div>
          <div className="gcw:flex gcw:items-center gcw:justify-center gcw:pt-1 gcw:pb-1">
            <PrimaryButton
              text={t("confirm", "Confirm")}
              className={
                "gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 pay-button"
              }
              onClick={() => {}}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerDetailsForm;
